import React from 'react';
import Container from '../../Atoms/Grid/Container';
import Grid from '../../Atoms/Grid/Grid';
import Column from '../../Atoms/Grid/Column';
import './GridShowcase.css';

/**
 * GridShowcase component
 *
 * A simplified demonstration of the ESS Design System grid system capabilities.
 */
const GridShowcase = () => {
  // Helper to render grid demo items
  const renderGridItems = (count, prefix = 'Item') => {
    return Array.from({ length: count }, (_, i) => (
      <div key={i} className="grid-demo-item">
        {prefix} {i + 1}
      </div>
    ));
  };

  return (
    <div className="grid-showcase">
      <Container>
        <h1>ESS Design System Grid System</h1>
        <p className="intro">
          Our grid system provides a flexible foundation for creating
          consistent, responsive layouts across our applications.
        </p>

        {/* SECTION 1: DESIGN TOKENS */}
        <section className="showcase-section">
          <h2>1. Design Tokens</h2>
          <p>
            Our grid system is built on design tokens that ensure consistent
            spacing and sizing.
          </p>

          <div className="example">
            <h3>Color Tokens</h3>
            <div className="token-examples">
              <div className="token-item">
                <div
                  className="token-visual"
                  style={{ backgroundColor: 'var(--color-brand-primary)' }}
                ></div>
                <code>--color-brand-primary</code>
              </div>
              <div className="token-item">
                <div
                  className="token-visual"
                  style={{ backgroundColor: 'var(--color-brand-secondary)' }}
                ></div>
                <code>--color-brand-secondary</code>
              </div>
              <div className="token-item">
                <div
                  className="token-visual"
                  style={{ backgroundColor: 'var(--color-gray-500)' }}
                ></div>
                <code>--color-gray-500</code>
              </div>
            </div>
          </div>

          <div className="example">
            <h3>Spacing Tokens</h3>
            <div className="spacing-examples">
              <div className="spacing-item">
                <div
                  className="spacing-visual"
                  style={{
                    width: 'var(--spacing-2)',
                    height: 'var(--spacing-2)',
                  }}
                ></div>
                <code>--spacing-2</code>
              </div>
              <div className="spacing-item">
                <div
                  className="spacing-visual"
                  style={{
                    width: 'var(--spacing-4)',
                    height: 'var(--spacing-4)',
                  }}
                ></div>
                <code>--spacing-4</code>
              </div>
              <div className="spacing-item">
                <div
                  className="spacing-visual"
                  style={{
                    width: 'var(--spacing-6)',
                    height: 'var(--spacing-6)',
                  }}
                ></div>
                <code>--spacing-6</code>
              </div>
            </div>
          </div>
        </section>

        {/* SECTION 2: CONTAINER COMPONENT */}
        <section className="showcase-section">
          <h2>2. Container Component</h2>
          <p>
            The Container component provides a centered, width-constrained
            wrapper for content to ensure consistent page layouts.
          </p>

          <div className="example">
            <div className="container-demo">
              <div className="container-inner">
                Content is contained within max-width (1200px)
              </div>
            </div>
            <div className="code-example">
              <pre>{`<Container>
  {/* Your content here */}
</Container>

// CSS implementation
.container {
  width: 100%;
  max-width: 1200px;
  margin-left: auto;
  margin-right: auto;
  padding-left: 1rem;
  padding-right: 1rem;
}`}</pre>
            </div>
          </div>
        </section>

        {/* SECTION 3: 12-COLUMN GRID */}
        <section className="showcase-section">
          <h2>3. Standard 12-Column Grid</h2>
          <p>
            Our Grid component supports a standard 12-column layout that can be
            customized with column spans.
          </p>

          <div className="example">
            <h3>Column Spans Example</h3>
            <Grid cols={12} className="demo-grid">
              <Column
                span={12}
                md={6}
                lg={12}
                xl={12}
                className="grid-demo-item"
              >
                col-xs-12
              </Column>
              <Column span={12} md={6} lg={4} xl={4} className="grid-demo-item">
                col-xs-6
              </Column>
              <Column span={12} md={6} lg={4} xl={4} className="grid-demo-item">
                col-xs-6
              </Column>
              <Column span={12} md={4} lg={4} xl={4} className="grid-demo-item">
                col-xs-4
              </Column>
              <Column span={12} md={4} lg={4} xl={4} className="grid-demo-item">
                col-xs-4
              </Column>
              <Column span={12} md={4} lg={4} xl={4} className="grid-demo-item">
                col-xs-4
              </Column>
              <Column span={12} md={3} lg={3} xl={3} className="grid-demo-item">
                col-xs-3
              </Column>
              <Column span={12} md={3} lg={3} xl={3} className="grid-demo-item">
                col-xs-3
              </Column>
              <Column span={12} md={3} lg={3} xl={3} className="grid-demo-item">
                col-xs-3
              </Column>
              <Column span={12} md={3} lg={3} xl={3} className="grid-demo-item">
                col-xs-3
              </Column>
            </Grid>
            <div className="code-example">
              <pre>{`<Grid cols={12}>
  <Column span={12} md={6} lg={12} xl={12} className="grid-demo-item">col-xs-12</Column>
  <Column span={12} md={6} lg={4} xl={4} className="grid-demo-item">col-xs-6</Column>
  <Column span={12} md={6} lg={4} xl={4} className="grid-demo-item">col-xs-6</Column>
  <Column span={12} md={4} lg={3} xl={3} className="grid-demo-item">col-xs-4</Column>
  <Column span={12} md={4} lg={3} xl={3} className="grid-demo-item">col-xs-4</Column>
  <Column span={12} md={4} lg={3} xl={3} className="grid-demo-item">col-xs-4</Column>
  <Column span={12} md={3} lg={3} xl={3} className="grid-demo-item">col-xs-3</Column>
  <Column span={12} md={3} lg={3} xl={3} className="grid-demo-item">col-xs-3</Column>
  <Column span={12} md={3} lg={3} xl={3} className="grid-demo-item">col-xs-3</Column>
  <Column span={12} md={3} lg={3} xl={3} className="grid-demo-item">col-xs-3</Column>
  {/* More items with column spans */}
</Grid>`}</pre>
            </div>
          </div>
        </section>

        {/* SECTION 4: PREDEFINED GRID LAYOUTS */}
        <section className="showcase-section">
          <h2>4. Predefined Grid Layouts</h2>
          <p>
            Our Design System provides several predefined grid layouts for
            common use cases.
          </p>

          <div className="example">
            <h3>2-Column Grid</h3>
            <Grid cols="2-cols" className="demo-grid">
              {renderGridItems(4)}
            </Grid>
            <div className="code-example">
              <pre>{`<Grid cols="2-cols">
  {/* Items will be arranged in 2 columns */}
  {/* Responsive by default: 2 columns throughout, consistent with other grid patterns */}
</Grid>

// CSS implementation 
.grid-2-cols {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(2, 1fr);

  @media (min-width: var(--small-screen-breakpoint, 768px)) {
    grid-template-columns: repeat(2, 1fr);
  }
}`}</pre>
            </div>
          </div>

          <div className="example">
            <h3>3-Column Grid</h3>
            <Grid cols="3-cols" className="demo-grid">
              {renderGridItems(6)}
            </Grid>
            <div className="code-example">
              <pre>{`<Grid cols="3-cols">
  {/* Items will be arranged in 3 columns */}
  {/* Responsive: 1 column on mobile, 2 on tablets, 3 on desktop */}
</Grid>

// CSS implementation
.grid-3-cols {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(1, 1fr);

  @media (min-width: var(--small-screen-breakpoint, 768px)) {
    grid-template-columns: repeat(2, 1fr);
  }

  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
}`}</pre>
            </div>
          </div>

          <div className="example">
            <h3>Auto-Fit Grid</h3>
            <Grid cols="auto-fit" className="demo-grid">
              {renderGridItems(5)}
            </Grid>
            <div className="code-example">
              <pre>{`<Grid cols="auto-fit">
  {/* Items will automatically fit based on available space */}
  {/* Minimum width of 280px per item */}
</Grid>

// CSS implementation
.grid-auto-fit {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
}`}</pre>
            </div>
          </div>

          <div className="example">
            <h3>Main with Sidebar</h3>
            <Grid cols="main-sidebar" className="demo-grid">
              <div className="grid-demo-item">Main Content</div>
              <div className="grid-demo-item">Sidebar</div>
            </Grid>
            <div className="code-example">
              <pre>{`<Grid cols="main-sidebar">
  <div>Main Content</div>
  <div>Sidebar</div>
</Grid>

// CSS implementation
.grid-main-sidebar {
  display: grid;
  gap: 1.5rem;
  grid-template-columns: 1fr;
  
  @media (min-width: 768px) {
    grid-template-columns: 2fr 1fr;
  }
}`}</pre>
            </div>
          </div>
        </section>

        {/* SECTION 5: CUSTOM GRID LAYOUTS */}
        <section className="showcase-section">
          <h2>5. Custom Grid Layouts</h2>
          <p>
            Our Grid component also supports custom layouts using CSS Grid
            syntax.
          </p>

          <div className="example">
            <h3>Custom Column Proportions</h3>
            <Grid cols="1fr 2fr 1fr" className="demo-grid">
              <div className="grid-demo-item">1fr</div>
              <div className="grid-demo-item">2fr (wider)</div>
              <div className="grid-demo-item">1fr</div>
            </Grid>
            <div className="code-example">
              <pre>{`<Grid cols="1fr 2fr 1fr">
  <div>1fr</div>
  <div>2fr (wider)</div>
  <div>1fr</div>
</Grid>`}</pre>
            </div>
          </div>

          <div className="example">
            <h3>Custom Gap Size</h3>
            <Grid cols="1fr 1fr 1fr" gap="3rem" className="demo-grid">
              {renderGridItems(3, 'Wide gap')}
            </Grid>
            <div className="code-example">
              <pre>{`<Grid cols="1fr 1fr 1fr" gap="3rem">
  {/* Items with custom gap size */}
</Grid>`}</pre>
            </div>
          </div>
        </section>

        {/* SECTION 6: RESPONSIVE CONTROL */}
        <section className="showcase-section">
          <h2>6. Responsive Control</h2>
          <p>
            Our Grid component provides fine-grained control over responsive
            behavior.
          </p>

          <div className="example">
            <h3>Custom Breakpoint and Small Screen Columns</h3>
            <Grid
              cols="repeat(4, 1fr)"
              smallScreenBreakpoint="1190px"
              smallScreenCols="repeat(2, 1fr)"
              className="demo-grid grid-responsive-example"
            >
              {renderGridItems(8)}
            </Grid>
            <div className="code-example">
              <pre>{`<Grid 
  cols="repeat(4, 1fr)" 
  smallScreenBreakpoint="1190px"
  smallScreenCols="repeat(2, 1fr)"
>
  {/* 
    Custom responsive behavior:
    - 2 columns below 1190px
    - 4 columns above 1190px
  */}
</Grid>`}</pre>
            </div>
            <p>
              The <code>smallScreenBreakpoint</code> property defines when
              layouts should change, while <code>smallScreenCols</code> defines
              what the layout should be below that breakpoint.
            </p>
          </div>
        </section>

        {/* SECTION 7: COLUMN COMPONENT RESPONSIVE EXAMPLES */}
        <section className="showcase-section">
          <h2>7. Column Component Responsive Behavior</h2>
          <p>
            The Column component works together with the Grid component to
            provide responsive control through various breakpoint properties.
          </p>

          <div className="example">
            <h3>Responsive Column Component</h3>
            <Grid cols={12} gap="1rem">
              <Column span={12} md={6} lg={4}>
                <div className="grid-demo-item">span=12 md=6 lg=4</div>
              </Column>
              <Column span={12} md={6} lg={4}>
                <div className="grid-demo-item">span=12 md=6 lg=4</div>
              </Column>
              <Column span={12} md={6} lg={4}>
                <div className="grid-demo-item">span=12 md=6 lg=4</div>
              </Column>
              <Column span={12} md={6} lg={3}>
                <div className="grid-demo-item">span=12 md=6 lg=3</div>
              </Column>
              <Column span={12} md={6} lg={3}>
                <div className="grid-demo-item">span=12 md=6 lg=3</div>
              </Column>
              <Column span={12} md={6} lg={3}>
                <div className="grid-demo-item">span=12 md=6 lg=3</div>
              </Column>
              <Column span={12} md={6} lg={3}>
                <div className="grid-demo-item">span=12 md=6 lg=3</div>
              </Column>
            </Grid>
            <div className="code-example">
              <pre>{`<Grid cols={12} gap="1rem">
  <Column span={12} md={6} lg={4}>
    <div className="grid-demo-item">Content</div>
  </Column>
  <Column span={12} md={6} lg={4}>
    <div className="grid-demo-item">Content</div>
  </Column>
  <Column span={12} md={6} lg={4}>
    <div className="grid-demo-item">Content</div>
  </Column>
</Grid>

// CSS Classes
.grid-column {
  /* Base styles for all columns */
}
.col-xs-12 { grid-column: span 12; }
.col-md-6 { grid-column: span 6; } /* Applied at 768px+ */
.col-lg-4 { grid-column: span 4; } /* Applied at 992px+ */`}</pre>
            </div>
            <div className="responsive-breakpoints">
              <p>
                <strong>Column Component Properties:</strong>
              </p>
              <ul>
                <li>
                  <code>span</code>: Base column span (default xs breakpoint)
                </li>
                <li>
                  <code>sm</code>: Column span for small screens (576px+)
                </li>
                <li>
                  <code>md</code>: Column span for medium screens (768px+)
                </li>
                <li>
                  <code>lg</code>: Column span for large screens (992px+)
                </li>
                <li>
                  <code>xl</code>: Column span for extra large screens (1200px+)
                </li>
              </ul>
            </div>
          </div>
        </section>

        {/* SECTION 8: ADDITIONAL UTILITIES */}
        <section className="showcase-section">
          <h2>8. Additional Utilities</h2>
          <p>
            Our Grid system includes several additional utilities for enhanced
            control.
          </p>

          <div className="example">
            <h3>Centered Grid Content</h3>
            <Grid cols="3-cols" center className="demo-grid">
              <div className="grid-demo-item tall">Centered content</div>
              <div className="grid-demo-item">Centered content</div>
              <div className="grid-demo-item tall">Centered content</div>
            </Grid>
            <div className="code-example">
              <pre>{`<Grid cols="3-cols" center>
  {/* Grid items with centered content */}
</Grid>`}</pre>
            </div>
          </div>

          <div className="example">
            <h3>Debug Mode</h3>
            <Grid cols="3-cols" debug className="demo-grid">
              {renderGridItems(3)}
            </Grid>
            <div className="code-example">
              <pre>{`<Grid cols="3-cols" debug>
  {/* Debug mode helps visualize the grid */}
</Grid>`}</pre>
            </div>
          </div>
        </section>
      </Container>
    </div>
  );
};

export default GridShowcase;
